<template>
  <b-modal ref="modal" :title="$t('sandoz.clone_comparison_title')" size="xl" no-close-on-backdrop @ok="confirm" :ok-title="$t('sandoz.confirm_selection')">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>
    <template v-else>
      <b-row>
        <!-- Left panel: Source contract items -->
        <b-col md="6">
          <h6 class="mb-3 font-weight-bold">
            <fa icon="file-alt" class="mr-1"/>
            {{ $t('sandoz.source_contract_items') }}
            <small class="text-muted">({{ sourceContract.contract_no || '-' }} / {{ sourceContract.contract_year }})</small>
          </h6>
          <div class="clone-tree-panel border rounded p-2" style="max-height: 60vh; overflow-y: auto;">
            <template v-for="address in filteredSourceAddresses">
              <div :key="'addr-' + address.id" class="mb-3">
                <div class="font-weight-bold text-muted small mb-1">
                  <fa icon="map-marker-alt" class="mr-1"/>
                  {{ address.company_name || address.customer_external_id }}
                </div>
                <template v-for="item in address.items">
                  <div :key="'src-item-' + item.product_id" class="clone-tree-item ml-2 py-1 border-bottom"
                       :class="{ 'text-danger': !isAvailableInNewYear(item) }">
                    <fa icon="cube" class="mr-1 text-primary" v-if="isAvailableInNewYear(item)"/>
                    <fa icon="exclamation-triangle" class="mr-1 text-danger" v-else/>
                    <span>{{ item.product_name }}</span>
                    <b-badge variant="danger" pill class="ml-1" v-if="!isAvailableInNewYear(item)">
                      {{ $t('sandoz.not_available_in_new_year') }}
                    </b-badge>
                    <span class="text-muted small ml-2" v-if="getItemQty(item)">
                      ({{ getItemQty(item) }})
                    </span>
                    <!-- Sub-items (level 2) -->
                    <template v-if="item.children && item.children.length">
                      <template v-for="child in item.children">
                        <div v-if="hasData(child)" :key="'src-child-' + child.product_id"
                             class="ml-4 py-1 small"
                             :class="{ 'text-danger': !isAvailableInNewYear(child) }">
                          <fa icon="angle-right" class="mr-1"/>
                          {{ child.product_name }}
                          <b-badge variant="danger" pill class="ml-1" v-if="!isAvailableInNewYear(child)">
                            {{ $t('sandoz.not_available_in_new_year') }}
                          </b-badge>
                          <!-- Sub-sub-items (level 3) -->
                          <template v-if="child.children && child.children.length">
                            <template v-for="grandchild in child.children">
                              <div v-if="hasData(grandchild)" :key="'src-gc-' + grandchild.product_id"
                                   class="ml-4 py-1"
                                   :class="{ 'text-danger': !isAvailableInNewYear(grandchild) }">
                                <fa icon="minus" class="mr-1"/>
                                {{ grandchild.product_name }}
                                <b-badge variant="danger" pill class="ml-1" v-if="!isAvailableInNewYear(grandchild)">
                                  {{ $t('sandoz.not_available_in_new_year') }}
                                </b-badge>
                              </div>
                            </template>
                          </template>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </b-col>

        <!-- Right panel: Items available in new year -->
        <b-col md="6">
          <h6 class="mb-3 font-weight-bold">
            <fa icon="plus-circle" class="mr-1 text-success"/>
            {{ $t('sandoz.new_contract_items') }}
            <small class="text-muted">({{ targetYear }})</small>
          </h6>
          <div class="clone-tree-panel border rounded p-2" style="max-height: 60vh; overflow-y: auto;">
            <template v-for="address in filteredSourceAddresses">
              <div :key="'new-addr-' + address.id" class="mb-3">
                <div class="font-weight-bold text-muted small mb-1">
                  <fa icon="map-marker-alt" class="mr-1"/>
                  {{ address.company_name || address.customer_external_id }}
                </div>
                <template v-for="item in address.items">
                  <div :key="'new-item-' + item.product_id" class="clone-tree-item ml-2 py-1 border-bottom"
                       v-if="isAvailableInNewYear(item)">
                    <fa icon="check-circle" class="mr-1 text-success"/>
                    <span>{{ item.product_name }}</span>
                    <span class="text-muted small ml-2" v-if="getItemQty(item)">
                      ({{ getItemQty(item) }})
                    </span>
                    <!-- Sub-items (level 2) -->
                    <template v-if="item.children && item.children.length">
                      <template v-for="child in item.children">
                        <div v-if="isAvailableInNewYear(child)" :key="'new-child-' + child.product_id"
                             class="ml-4 py-1 small">
                          <fa icon="angle-right" class="mr-1 text-success"/>
                          {{ child.product_name }}
                          <!-- Sub-sub-items (level 3) -->
                          <template v-if="child.children && child.children.length">
                            <template v-for="grandchild in child.children">
                              <div v-if="isAvailableInNewYear(grandchild)" :key="'new-gc-' + grandchild.product_id"
                                   class="ml-4 py-1">
                                <fa icon="minus" class="mr-1 text-success"/>
                                {{ grandchild.product_name }}
                              </div>
                            </template>
                          </template>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
export default {
  props: {
    sourceContract: {
      type: Object,
      default: () => ({})
    },
    availableProductMasterIds: {
      type: Array,
      default: () => []
    },
    targetYear: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      dataReady: false
    }
  },
  watch: {
    sourceContract: {
      handler(val) {
        if (val && val.addresses && val.addresses.length) {
          this.dataReady = true
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    sourceAddresses() {
      return this.sourceContract.addresses || []
    },
    filteredSourceAddresses() {
      return this.sourceAddresses
        .map(addr => ({
          ...addr,
          items: (addr.items || []).filter(item => this.hasData(item))
        }))
        .filter(addr => addr.items.length > 0)
    },
    totalSourceItems() {
      let count = 0
      this.filteredSourceAddresses.forEach(addr => {
        if (addr.items) {
          count += this.countItems(addr.items)
        }
      })
      return count
    },
    availableCount() {
      let count = 0
      this.filteredSourceAddresses.forEach(addr => {
        if (addr.items) {
          count += this.countAvailableItems(addr.items)
        }
      })
      return count
    }
  },
  methods: {
    show() {
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    confirm() {
      this.$emit('confirm', {
        sourceContract: this.sourceContract,
        availableProductMasterIds: this.availableProductMasterIds,
        targetYear: this.targetYear
      })
    },
    isAvailableInNewYear(item) {
      // Compare using product_master_id (business key, e.g. "99-019")
      const pmId = item.product_master_id
      if (!pmId) return false
      return this.availableProductMasterIds.includes(pmId)
    },
    hasData(item) {
      // Check if item has a price or any quantity set
      if (item.price && parseFloat(item.price) > 0) return true
      const d = item.delivery
      if (d && (d.qty_yearly || d.qty_q1 || d.qty_q2 || d.qty_q3 || d.qty_q4)) return true
      // Also include if any child has data
      if (item.children && item.children.length) {
        return item.children.some(child => this.hasData(child))
      }
      return false
    },
    getItemQty(item) {
      const delivery = item.delivery
      if (!delivery) return null
      const qty = delivery.qty_yearly || delivery.qty_q1 || delivery.qty_q2 || delivery.qty_q3 || delivery.qty_q4
      return qty || null
    },
    countItems(items) {
      let count = 0
      items.forEach(item => {
        count++
        if (item.children && item.children.length) {
          count += this.countItems(item.children)
        }
      })
      return count
    },
    countAvailableItems(items) {
      let count = 0
      items.forEach(item => {
        if (this.isAvailableInNewYear(item)) count++
        if (item.children && item.children.length) {
          count += this.countAvailableItems(item.children)
        }
      })
      return count
    }
  }
}
</script>

<style scoped>
.clone-tree-panel {
  background: #f8f9fa;
}
.clone-tree-item {
  font-size: 0.9rem;
}
</style>
